import { Grid } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IGovernanceItem from "../../../../interfaces/IGovernanceItem";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import FormHelper from "../../../../helpers/FormHelper";
import GovernanceItemService from '../../../../services/GovernanceItemService';
import SelectField from "../../../../components/SelectField";
import { useUserContext } from "../../../../contexts/UserContext";
import RichTextEditor from "../../../../components/RichTextEditor";


const AddEditDialog: React.FunctionComponent<IAddEditDialogProps<IGovernanceItem>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();

    const [name, setName] = useState<string>('');
    const [orderBy, setOrderBy] = useState<number>(0);
    const [required, setRequired] = useState<boolean>(false);
    const [actived, setActived] = useState<boolean>(true);
    const [description, setDescription] = useState<string>('');
    const [roomTypes, setRoomTypes] = useState<number[]>([]);


    useEffect(() => {
        if (open) {
            if (data) {
                setName(data.name);
                setOrderBy(data.orderBy);
                setRequired(data.required);
                setActived(Number(data.registrationStatusId) === RegistrationStatusEnum.Actived);
                setDescription(data.description);
                setRoomTypes(data.roomTypes);
            } else {
                setName('');
                setOrderBy(0);
                setRequired(false);
                setActived(true);
                setRoomTypes([]);
                setDescription('');
            }
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        GovernanceItemService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data ? 'edit' : 'add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('name')} 
                        name='name' 
                        value={name} 
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 100, setName)}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        type="number"
                        label={translate('ordering')} 
                        name='orderBy' 
                        value={orderBy} 
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 10, setOrderBy)}
                        required={true}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <SelectField
                        label={translate('roomTypes')}
                        name='roomTypes'
                        value={roomTypes.length > 0 ? roomTypes : [0]}
                        multiple={true}
                        onChange={e => {
                            const values = e.target.value as number[];
                            setRoomTypes(roomTypes.length > 0 && values.includes(0) ? [] : values.filter(v => v !== 0));
                        }}
                        data={[{ value: 0, text: translate('all') }].concat((enterprise?.partnerPms.roomTypes ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item md={9} xs={12} className='textalign-right'>
                    <Switch
                        label={translate('required')}
                        name='required'
                        value={required}
                        checked={required}
                        onChange={e => setRequired(e.target.checked)}
                    />
                </Grid>

                <Grid item md={3} xs={12} className='textalign-right'>
                    <Switch
                        label={translate('actived')}
                        name='registrationStatusId'
                        value={actived ? RegistrationStatusEnum.Actived : RegistrationStatusEnum.Inactived}
                        checked={actived}
                        onChange={e => setActived(e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginTop={2}>
                    <RichTextEditor 
                        label={translate('description')}
                        name="description"
                        value={description}
                        handleChange={value => setDescription(value)}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;